import { CloseHealth } from "@common/types/closeHealth";
import {
    DEFAULT_ESTIMATED_TIME_CELL_VARIANT,
    EstimatedTimeCellVariantSchema,
    TaskProgressCellVariantSchema,
} from "@common/types/views/clientsWorkOverviewView";
import { isBusyClosesDemoId } from "@components/demo/busyClosesDemo/busyClosesDemoIds";
import {
    ActionsCell,
    CellText,
    type DataGridCellProps,
    EmptyCellValue,
    PersonCell,
} from "@design-system/DataGrid";
import { parseCloseId } from "@hooks/useOpenWorkRow";
import { WorkDeliveryDateWithOverdue } from "../cells/WorkDeliveryDateCell";
import { WorkEstimatedTime } from "../cells/WorkEstimatedTimeCell";
import { WorkHealth } from "../cells/WorkHealthCell";
import { WorkTaskProgress } from "../cells/WorkTaskProgressCell";
import type { ClientsWorkOverviewRow } from "../clientsWorkOverviewViewConfig";
import { WorkRowActionItems } from "../WorkRowActions";
import { useWorkListActions } from "./WorkListActionsContext";
import { WorkRowPresence } from "./WorkRowPresence";
import { getWorkEstimatedTime, UNKNOWN_APPLIED_BY } from "./workListValues";

type WorkListCellProps = DataGridCellProps<ClientsWorkOverviewRow>;

/** After the Service name: a group's summary in the secondary tone, or a work item's presence stack. */
export const WorkNameTrailing = ({ context, meta }: WorkListCellProps) => {
    const row = context.row.original;
    if (row.rowType === "work") {
        return <WorkRowPresence row={row} />;
    }
    if (row.rowType === "group" && row.summary) {
        return (
            <CellText
                text={` · ${row.summary}`}
                overflow={meta.overflow}
                tone="secondary"
            />
        );
    }
    return null;
};

/** Health as its chip, with the reason on hover. */
export const WorkHealthChip = ({ context, meta }: WorkListCellProps) => {
    const row = context.row.original;
    if (row.rowType !== "work" || row.health === CloseHealth.NONE) {
        return <EmptyCellValue empty={meta.empty} />;
    }
    return <WorkHealth row={row} />;
};

/** Finished tasks in the column's variant, for work with tasks. */
export const WorkProgressCell = ({
    context,
    meta,
    variant,
}: WorkListCellProps) => {
    const row = context.row.original;
    if (row.rowType !== "work" || row.totalTaskCount === 0) {
        return <EmptyCellValue empty={meta.empty} />;
    }
    const parsed = TaskProgressCellVariantSchema.safeParse(variant);
    return (
        <WorkTaskProgress
            row={row}
            variant={parsed.success ? parsed.data : "count"}
        />
    );
};

/**
 * Estimated time in the column's variant, with the per-member breakdown on
 * hover; a dash for work with no estimates, as ViewTable showed it.
 */
export const WorkEstimatedTimeListCell = ({
    context,
    meta,
    variant,
}: WorkListCellProps) => {
    const row = context.row.original;
    if (row.rowType !== "work") {
        return <EmptyCellValue empty={meta.empty} />;
    }
    if (getWorkEstimatedTime(row) === undefined) {
        return <EmptyCellValue empty="dash" />;
    }
    const parsed = EstimatedTimeCellVariantSchema.safeParse(variant);
    return (
        <WorkEstimatedTime
            row={row}
            variant={
                parsed.success
                    ? parsed.data
                    : DEFAULT_ESTIMATED_TIME_CELL_VARIANT
            }
        />
    );
};

/** A client's Open (its workspace), or a work's actions menu; group rows have neither. */
export const WorkListActionsCell = ({ context }: WorkListCellProps) => {
    const actions = useWorkListActions();
    const row = context.row.original;
    if (
        !actions ||
        row.rowType === "group" ||
        (row.rowType === "work" &&
            (!parseCloseId(row.closeId) || isBusyClosesDemoId(row.closeId)))
    ) {
        return null;
    }
    if (row.rowType === "workspace") {
        return (
            <ActionsCell>
                <ActionsCell.Button
                    variant="primary"
                    onClick={() => actions.onOpenWorkspace(row.workspaceId)}
                >
                    Open
                </ActionsCell.Button>
            </ActionsCell>
        );
    }
    return (
        <ActionsCell>
            <ActionsCell.Menu>
                <WorkRowActionItems
                    row={row}
                    callbacks={actions.callbacks}
                    onConfirmationRequest={actions.onConfirmationRequest}
                />
            </ActionsCell.Menu>
        </ActionsCell>
    );
};

/** Who applied the work; "Unknown" (not a member) is plain text, with no avatar. */
export const WorkAppliedByListCell = (props: WorkListCellProps) =>
    props.context.getValue() === UNKNOWN_APPLIED_BY ? (
        <CellText
            text={UNKNOWN_APPLIED_BY.name}
            overflow={props.meta.overflow}
        />
    ) : (
        <PersonCell {...props} fallback={undefined} />
    );

/** Work end; late open work says how overdue it is on hover. */
export const WorkDeliveryDateOverdue = ({
    context,
    meta,
}: WorkListCellProps) => {
    const row = context.row.original;
    if (row.rowType !== "work" || !row.workDeliveryDate) {
        return <EmptyCellValue empty={meta.empty} />;
    }
    return <WorkDeliveryDateWithOverdue row={row} />;
};
